import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Spin,
  Tag,
  Form,
  Modal,
  Select,
  Input,
  InputNumber,
  Table,
  Popover,
  Space,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import moment from "moment";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  AuditOutlined,
  EyeOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSingleSaleInvoice,
  ChangeSaleInvoiceStatus,
  registerSaleInvoicePayment,
  getInvoicePaymentsDetails,
  getTransactionQRCode,
} from "../../../network/network";
import {
  SaleInvoiceStatus,
  UserType,
  AddonName,
  ZatcaInvoiceStatus,
  ZatcaInvoiceSubtype,
  zatcaDocumentTypes,
  zatcaInvoiceSubtypes,
  zatcaInvoiceStatuses,
} from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import ReactToPrint from "react-to-print";
import TaxCalculator from "../../../components/TaxCalculator";
import { handleApiError } from "../../../utils/errorHandler";

const getQRCodeSource = (qrCode) => {
  if (!qrCode) return "";

  const value = String(qrCode).trim();

  if (!value) return "";

  if (value.startsWith("data:image/")) return value;

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
  }

  if (value.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${value}`;
  }

  if (value.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${value}`;
  }

  if (value.startsWith("R0lGOD")) {
    return `data:image/gif;base64,${value}`;
  }

  if (value.startsWith("PHN2Zy")) {
    return `data:image/svg+xml;base64,${value}`;
  }

  return "";
};

const getZatcaValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value.value || value.id || value.name;
  return value;
};

const getZatcaOption = (options, value) => {
  const id = getZatcaValue(value);

  return options.find(
    (item) => item.id === id || item.value === id || item.name === id,
  );
};

const getZatcaOptionTag = (options, value, t) => {
  const id = getZatcaValue(value);
  const option = getZatcaOption(options, id);

  if (option) {
    return <Tag color={option.color}>{t(option.text)}</Tag>;
  }

  if (value?.label) {
    return <Tag>{value.label}</Tag>;
  }

  return id ? <Tag>{t(id)}</Tag> : null;
};

const getPrintableZatcaStatus = (status, t) => {
  const normalizedStatus = getZatcaValue(status);

  switch (normalizedStatus) {
    case ZatcaInvoiceStatus.Pending:
    case ZatcaInvoiceStatus.Submitted:
      return {
        text: t("verifying_with_zatca"),
        color: "var(--color-info)",
      };
    case ZatcaInvoiceStatus.Accepted:
      return {
        text: t("accepted"),
        color: "var(--color-success)",
      };
    case ZatcaInvoiceStatus.AcceptedWithWarnings:
      return {
        text: t("accepted_with_warnings"),
        color: "var(--color-success)",
      };
    case ZatcaInvoiceStatus.Failed:
      return {
        text: t("zatca_invoice_report_failed"),
        color: "orange",
      };
    case ZatcaInvoiceStatus.Rejected:
      return {
        text: t("rejected_by_zatca"),
        color: "var(--color-danger)",
      };
    default:
      return normalizedStatus
        ? { text: normalizedStatus, color: "var(--color-text-muted)" }
        : null;
  }
};

const ViewSalesInvoice = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const [data, setData] = useState(false);
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [reload, setReload] = useState(false);
  const [transactionQRCode, setTransactionQRCode] = useState("");
  let { salesInvoiceId } = useParams();
  const { t } = useTranslation();
  const isRtl = appConfig?.direction === "rtl";
  const getLocalizedName = (item) =>
    isRtl
      ? item?.name_ar || item?.name || item?.name_en || ""
      : item?.name_en || item?.name || item?.name_ar || "";
  const getPaymentMethodLabel = (method) => {
    const normalizedMethod = String(method || "").toLowerCase();
    const translationKeys = {
      cash: "cash",
      card: "card",
      credit_card: "credit_card",
      multi: "multi_payment",
      multi_payment: "multi_payment",
    };
    return translationKeys[normalizedMethod]
      ? t(translationKeys[normalizedMethod])
      : method || "-";
  };
  const [salesInvoiceForm] = Form.useForm();
  const saleInvoiceSlipRef = useRef();
  const navigate = useNavigate();

  const is_zatca_e_invoicing_active = authUser?.active_addons?.some(
    (addon) => addon?.id && addon?.name === AddonName.ZatcaEInvoicing,
  );
  const canShowZatcaInvoiceData = !!(
    is_zatca_e_invoicing_active && data?.zatca?.is_zatca_invoice
  );
  const zatcaQRCode = canShowZatcaInvoiceData
    ? data?.zatca?.qr_code || data?.zatca_qr_code || ""
    : "";
  const zatcaQRCodeSource = getQRCodeSource(zatcaQRCode);
  const qrCodeSource = zatcaQRCodeSource || getQRCodeSource(transactionQRCode);

  useEffect(() => {
    setLoading(true);
    getSingleSaleInvoice(salesInvoiceId)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setData(response.data.data.saleInvoice);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  }, [salesInvoiceId, reload, t]);

  useEffect(() => {
    setLoading(true);
    getInvoicePaymentsDetails(salesInvoiceId)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setPaymentData(response?.data?.data?.saleInvoicePayments || []);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  }, [salesInvoiceId, reload, t]);

  useEffect(() => {
    if (!data.id || zatcaQRCodeSource) return;

    getTransactionQRCode(data.id)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTransactionQRCode(response.data.data.qr_code);
      })
      .catch((error) => handleApiError(error, t));
  }, [data.id, zatcaQRCodeSource, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "sales-invoice",
      breadcrumbName: t("sales_invoice"),
      icon: <AuditOutlined />,
    },
    {
      path: "view-sales-invoice",
      breadcrumbName: t("view_sales_invoice"),
      icon: <EyeOutlined />,
    },
  ];

  const updateStatus = (id) => {
    const formData = new FormData();
    if (id) {
      formData.append("id", id);
    }
    ChangeSaleInvoiceStatus(formData)
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          // Success
          NotificationWithIcon(
            "success",
            t("entity_updated"),
            t("entity_updated_successfully"),
          );
          // navigate("/sales-invoice");
          setReload(!reload);
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const updatePayment = () => {
    salesInvoiceForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoadingBTN(true);
        // Sending Request to API
        registerSaleInvoicePayment(values)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              // Success
              NotificationWithIcon(
                "success",
                t("entity_updated"),
                t("entity_updated_successfully"),
              );
              setReload(!reload);
              setModalVisible(false);
              salesInvoiceForm.resetFields();
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            // Set Loading False
            setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const showTableModal = () => {
    // Show Modal
    setModalVisible(true);
    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    salesInvoiceForm.resetFields();
    // Set Form Fields
    salesInvoiceForm.setFieldsValue({
      payment: data.balance,
      balance: data.balance,
      transaction_id: data.id,
    });
  };

  const handleCancel = () => {
    // Reset Form Fields
    salesInvoiceForm.resetFields();
    setModalVisible(false);
  };

  // paymentTableColumns Columns
  const paymentTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("created_at"),
      dataIndex: "created_at",
      render: (created_at) => {
        return <p>{moment(created_at).format("MMM. D, YYYY hh:mm a")}</p>;
      },
    },
    {
      title: t("created_by"),
      render: (record) => {
        return <>{record.created_by.name ? record.created_by.name : "-"}</>;
      },
    },
    {
      title: t("payment_types"),
      render: (record) => {
        return <>{record.payment_method ? record.payment_method : "-"}</>;
      },
    },
    {
      title: t("amount"),
      render: (record) => {
        return (
          <>
            <p>
              <CurrencyFormatter value={record.payment} />
            </p>
          </>
        );
      },
    },
    {
      title: t("balance"),
      render: (record) => {
        return (
          <>
            <p>
              <CurrencyFormatter value={record.balance} />
            </p>
          </>
        );
      },
    },
  ];

  const isVatExempt =
    authUser?.is_vat_exempt === 1 || authUser?.is_vat_exempt === true;

  const renderZatcaStatusBlock = () => {
    const zatca = data?.zatca;

    if (!canShowZatcaInvoiceData) return null;

    const invoiceSubtype = zatca.is_b2b
      ? ZatcaInvoiceSubtype.Standard
      : zatca.is_b2c
        ? ZatcaInvoiceSubtype.Simplified
        : getZatcaValue(zatca.invoice_subtype);

    const warningsCount = Number(zatca.warnings_count || 0);
    const errorsCount = Number(zatca.errors_count || 0);

    return (
      <Col
        className="gutter-row"
        xs={24}
        sm={24}
        md={24}
        lg={24}
        xl={24}
        xxl={24}
      >
        <div
          className="app-detail-label"
        >
          {t("zatca_status")}
        </div>

        <div className="app-detail-value">
          <Space direction="vertical" size={8}>
            <Space size={4} wrap>
              {getZatcaOptionTag(zatcaInvoiceStatuses, zatca.status, t)}
              {getZatcaOptionTag(zatcaDocumentTypes, zatca.document_type, t)}
              {getZatcaOptionTag(zatcaInvoiceSubtypes, invoiceSubtype, t)}
            </Space>

            <Space size={4} wrap>
              {warningsCount > 0 ? (
                <Tag color="gold">
                  {warningsCount} {t("warnings")}
                </Tag>
              ) : null}

              {errorsCount > 0 ? (
                <Tag color="red">
                  {errorsCount} {t("zatca_errors")}
                </Tag>
              ) : null}

              {warningsCount === 0 && errorsCount === 0 ? (
                <Tag color="green">{t("no_warnings")}</Tag>
              ) : null}
            </Space>
          </Space>
        </div>
      </Col>
    );
  };

  const renderPrintableZatcaStatus = () => {
    const printableStatus = canShowZatcaInvoiceData
      ? getPrintableZatcaStatus(data?.zatca?.status, t)
      : null;

    if (!printableStatus) return null;

    return (
      <div
        style={{
          color: printableStatus.color,
          fontSize: "11px",
          fontWeight: 600,
          marginTop: "6px",
        }}
      >
        {printableStatus.text}
      </div>
    );
  };

  const renderPrintableQRCode = () => {
    if (qrCodeSource) {
      return (
        <img
          style={{
            maxHeight: "70px",
            maxWidth: "200px",
          }}
          src={qrCodeSource}
          alt={t("qr")}
        />
      );
    }

    const status = getZatcaValue(data?.zatca?.status);

    if (
      canShowZatcaInvoiceData &&
      [ZatcaInvoiceStatus.Pending, ZatcaInvoiceStatus.Submitted].includes(
        status,
      )
    ) {
      return (
        <div
          style={{
            width: "110px",
            minHeight: "70px",
            marginRight: "auto",
            border: "1px dashed var(--color-text-muted)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            padding: "6px",
            textAlign: "center",
          }}
        >
          {t("verifying_with_zatca")}
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("view_sales_invoice")}
          onBack={() => window.history.back()}
          ghost={false}
          className="app-p-0"
          extra={[
            data.sale_invoice_status === SaleInvoiceStatus.Draft && (
              <Button
                type="primary"
                key="edit"
                onClick={() => {
                  navigate("/sales-invoice/" + data.id + "/edit", {
                    state: data,
                  });
                }}
              >
                {t("invoice_edit")}
              </Button>
            ),
            (authUser.can_add_pay_sales_invoice ||
              authUser.type === UserType.BusinessOwner) &&
              data.sale_invoice_status === SaleInvoiceStatus.Draft && (
                <Button
                  key="updateStatus"
                  type="primary"
                  onClick={() => updateStatus(data.id)}
                >
                  {t("issue_invoice")}
                </Button>
              ),
            (authUser.can_add_pay_sales_invoice ||
              authUser.type === UserType.BusinessOwner) &&
              (data.sale_invoice_status === SaleInvoiceStatus.IssueInvoice ||
                data.sale_invoice_status ===
                  SaleInvoiceStatus.PartialPaidInvoice) && (
                <Button
                  key="showTableModal"
                  type="primary"
                  onClick={showTableModal}
                >
                  + {t("register_payment")}
                </Button>
              ),
            data.sale_invoice_status !== SaleInvoiceStatus.Draft && (
              <ReactToPrint
                trigger={() => (
                  <Button key="print" type="primary">
                    {t("print")}
                  </Button>
                )}
                content={() => saleInvoiceSlipRef.current}
                documentTitle={data.uid}
                key="1"
              />
            ),
          ]}
        />
      </Card>
      <Spin spinning={loading}>
        <Card
          style={{
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          {data && (
            <Row gutter={[20, 20]}>
              {data.sale_invoice_status === SaleInvoiceStatus.PaidInvoice && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div
                    className="app-detail-label"
                  >
                    {t("transaction_id")}
                  </div>
                  <div className="app-detail-value">
                    {data.uid ? data.uid : "-"}
                  </div>
                </Col>
              )}
              {data.sale_invoice_status !== SaleInvoiceStatus.Draft && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div
                    className="app-detail-label"
                  >
                    {t("invoice_id")}
                  </div>
                  <div className="app-detail-value">
                    {data.invoice_number ? data.invoice_number : "-"}
                  </div>
                </Col>
              )}
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={8}
                xxl={8}
              >
                <div
                  className="app-detail-label"
                >
                  {t("status")}
                </div>
                <div className="app-detail-value">
                  {data.sale_invoice_status === SaleInvoiceStatus.Draft && (
                    <Tag color="warning">{t("draft_invoices")}</Tag>
                  )}
                  {data.sale_invoice_status ===
                    SaleInvoiceStatus.IssueInvoice && (
                    <Tag color="warning">{t("open_invoices")}</Tag>
                  )}
                  {data.sale_invoice_status ===
                    SaleInvoiceStatus.PartialPaidInvoice && (
                    <Tag color="blue">{t("partially_paid_invoices")}</Tag>
                  )}
                  {data.sale_invoice_status ===
                    SaleInvoiceStatus.PaidInvoice &&
                    (data.reference_transaction ? (
                      <>
                        <Tag color="error">{t("status_refunded")}</Tag>
                        {t("reference_no")}:{" "}
                        <strong>{data.reference_transaction.uid}</strong>
                      </>
                    ) : (
                      <Tag color="green">{t("paid_invoices")}</Tag>
                    ))}
                </div>
              </Col>
              {authUser.type === UserType.BusinessOwner && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div
                    className="app-detail-label"
                  >
                    {t("branch")}
                  </div>
                  <div className="app-detail-value">
                    {data.branch ? data.branch : "-"}
                  </div>
                </Col>
              )}
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={8}
                xxl={8}
              >
                <div
                  className="app-detail-label"
                >
                  {t("created_at")}
                </div>
                <div className="app-detail-value">
                  {moment(
                    data.sale_invoice_status !== SaleInvoiceStatus.Draft
                      ? data?.create_invoice_date
                      : data?.created_at,
                  ).format("MMM. D, YYYY hh:mm a")}
                </div>
              </Col>
              {data.sale_invoice_status === SaleInvoiceStatus.PaidInvoice && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div
                    className="app-detail-label"
                  >
                    {t("paid_at")}
                  </div>
                  <div className="app-detail-value">
                    <div>
                      {moment(data?.created_at).format("MMM. D, YYYY hh:mm a")}{" "}
                      {data.sale_invoice_status ===
                        SaleInvoiceStatus.PaidInvoice &&
                        moment(data.invoice_due_date).diff(
                          moment(data.created_at),
                          "seconds",
                        ) < 0 && <Tag color="error">{t("late_payment")}</Tag>}
                    </div>
                  </div>
                </Col>
              )}
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={8}
                xxl={8}
              >
                <div
                  className="app-detail-label"
                >
                  {t("due_date")}
                </div>
                <div className="app-detail-value">
                  {data.invoice_due_date ? (
                    <div>
                      {moment(data.invoice_due_date).format("MMM. D, YYYY")}{" "}
                      {data.sale_invoice_status !==
                        SaleInvoiceStatus.PaidInvoice &&
                        moment(data.invoice_due_date).diff(
                          moment(),
                          "seconds",
                        ) < 0 && <Tag color="error">{t("expired")}</Tag>}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              </Col>
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={8}
                xxl={8}
              >
                <div
                  className="app-detail-label"
                >
                  {t("customer_name")}
                </div>
                <div className="app-detail-value">
                  {getLocalizedName(data.customer) || "-"}
                </div>
              </Col>
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={8}
                xxl={8}
              >
                <div
                  className="app-detail-label"
                >
                  {t("amount")}
                </div>
                <div className="app-detail-value">
                  {data.amount_charged ? (
                    <CurrencyFormatter value={data.amount_charged} />
                  ) : (
                    "-"
                  )}
                </div>
              </Col>
              {data.sale_invoice_status !== SaleInvoiceStatus.PaidInvoice && (
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div
                    className="app-detail-label"
                  >
                    {t("balance")}
                  </div>
                  <div className="app-detail-value">
                    {data.balance ? (
                      <CurrencyFormatter value={data.balance} />
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>
              )}

              {renderZatcaStatusBlock()}

              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <div className="app-detail-value">
                  {data.items && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "1px dashed var(--color-text-muted)",
                          padding: "10px 0",
                        }}
                      >
                        <span
                          className="app-font-bold"
                        >
                          {t("items")}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontWeight: "bold",
                          }}
                        >
                          <span className="app-w-100px app-text-center">
                            {t("unit_price")}
                          </span>
                          <span
                            style={{
                              width: "250px",
                              textAlign:
                                appConfig.direction === "ltr"
                                  ? "right"
                                  : "left",
                            }}
                          >
                            {t("price_including_tax")}
                          </span>
                        </div>
                      </div>
                      <div
                        style={{
                          paddingBottom: "10px",
                          borderBottom: "1px solid var(--color-text-muted)",
                        }}
                      >
                        {data.items?.map((item) => (
                          <div
                            className="app-summary-row"
                            key={"product_items_" + item.id}
                          >
                            <span>
                              {appConfig.direction === "ltr" ? (
                                <>
                                  {item.quantity} x <b>{item.name_en}</b>
                                </>
                              ) : (
                                <>
                                  {item.quantity} x <b>{item.name}</b>
                                </>
                              )}

                              {item.tags && item.tags.length > 0 && (
                                <div style={{ marginTop: 4 }}>
                                  {item.tags.map((tag) => (
                                    <Tag key={tag.id ?? tag} color="blue">
                                      {appConfig.direction === "rtl"
                                        ? tag.name_ar || tag.name
                                        : tag.name || tag.name_en}
                                    </Tag>
                                  ))}
                                </div>
                              )}
                            </span>
                            <div
                              className="app-flex-between-center"
                            >
                              <span
                                className="app-w-100px app-text-center"
                              >
                                <CurrencyFormatter
                                  value={item.price}
                                  currencyCode={false}
                                />
                              </span>
                              <span
                                style={{
                                  width: "250px",
                                  textAlign:
                                    appConfig.direction === "ltr"
                                      ? "right"
                                      : "left",
                                }}
                              >
                                <CurrencyFormatter
                                  value={item.subtotal}
                                  currencyCode={false}
                                />
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div
                        className="app-summary-row"
                      >
                        {t("total_without_tax")}
                        <span>
                          <CurrencyFormatter
                            value={data.amount_charged - data.tax}
                          />
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 0",
                          borderBottom: "1px solid var(--color-text-muted)",
                        }}
                      >
                        <div>
                          {t("total_taxes")}
                          {data.tax > 0 && (
                            <Popover
                              content={<TaxCalculator data={data} />}
                              trigger="click"
                            >
                              <InfoCircleOutlined
                                className="app-info-icon"
                              />
                            </Popover>
                          )}
                        </div>
                        <span>
                          <CurrencyFormatter value={data.tax} />
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 0 0",
                          fontWeight: "bold",
                        }}
                      >
                        {isVatExempt ? t("total") : t("total_with_tax")}

                        <b>
                          <CurrencyFormatter value={data.amount_charged} />
                        </b>
                      </div>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Card>
        {paymentData.length > 0 && (
          <Card style={{ marginTop: "20px" }}>
            <Table
              columns={paymentTableColumns}
              dataSource={paymentData}
              rowKey="id"
              pagination={false}
            />
          </Card>
        )}
      </Spin>
      <Modal
        open={modalVisible}
        title={t("register_payment")}
        onOk={updatePayment}
        onCancel={handleCancel}
        footer={[
          <Button type="default" key="back" loading={loadingBTN} onClick={handleCancel}>
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={updatePayment}
          >
            {t("confirm")}
          </Button>,
        ]}
      >
        <Form
          name="regsiter-payment-form"
          layout="vertical"
          onFinish={updatePayment}
          className="app-form-full"
          form={salesInvoiceForm}
        >
          <Form.Item
            name="payment"
            label={t("payment")}
            rules={[
              {
                required: true,
                message: t("validate_entity_required"),
              },
            ]}
            hasFeedback
          >
            <InputNumber
              min={0}
              max={data.balance}
              className="app-w-full"
              disabled={true}
            />
          </Form.Item>

          <Form.Item
            name="payment_method"
            label={t("select_payment_method")}
            rules={[
              {
                required: true,
                message: t("validate_entity_required"),
              },
            ]}
            hasFeedback
          >
            <Select
              allowClear
              options={[
                {
                  value: "bank",
                  label: t("bank"),
                },
                {
                  value: "cash",
                  label: t("cash"),
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="balance" label={t("balance")}>
            <Input disabled={true} />
          </Form.Item>
          <Form.Item name="transaction_id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
      {/* invoice printing code */}
      {data && (
        <div
          style={{
            padding: "10px 20px",
            position: "absolute",
            top: "0",
            zIndex: "-9999",
          }}
        >
          <div
            ref={saleInvoiceSlipRef}
            style={{
              padding: "20px",
              fontSize: "12px",
              direction: isRtl ? "rtl" : "ltr",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
              }}
            >
              <div style={{ width: "265px" }}>
                <strong>
                  <span>{t("invoice_id")}:</span>{" "}
                  <span>{data.invoice_number}</span>
                </strong>
                <div>
                  <span>{t("issue_date")}:</span>{" "}
                  <span>
                    {moment(data.create_invoice_date).format(
                      "MMM. D, YYYY hh:mm a",
                    )}
                  </span>
                </div>
                {data.sale_invoice_status ===
                  SaleInvoiceStatus.PaidInvoice &&
                  (data.reference_transaction ? (
                    <div>
                      <span>{t("transaction_reference")}:</span>{" "}
                      <span>{data.reference_transaction?.uid}</span>
                    </div>
                  ) : (
                    <div>
                      <span>{t("payment_method")}:</span>{" "}
                      <span>
                        {paymentData &&
                          getPaymentMethodLabel(paymentData[0]?.payment_method)}
                      </span>
                    </div>
                  ))}
                {getLocalizedName(data.customer) && (
                  <div>
                    <span>{t("buyer_company_name")}:</span>
                    <span>{getLocalizedName(data.customer)}</span>
                  </div>
                )}
                {data.customer?.vat && (
                  <div>
                    <span>{t("buyer_company_vat")}:</span>
                    <span>{data.customer.vat}</span>
                  </div>
                )}
              </div>
              <strong
                style={{
                  width: "265px",
                  textAlign: "center",
                }}
              >
                {data.reference_transaction ? (
                  <span>{t("refund_invoice")}</span>
                ) : isVatExempt ? (
                  <span>{t("simplified_invoice")}</span>
                ) : (
                  <span>{t("simplified_tax_invoice")}</span>
                )}
                <div>
                  {data.uid ? (
                    data.uid
                  ) : (
                    <Tag color="error" style={{ marginTop: "5px" }}>
                      {t("unpaid_invoice")}
                    </Tag>
                  )}
                </div>
              </strong>
              <div
                style={{
                  width: "265px",
                  textAlign: "end",
                }}
              >
                <img
                  id="store-logo"
                  style={{
                    maxHeight: "70px",
                    maxWidth: "200px",
                  }}
                  src={authUser.logo}
                  alt={t("business_logo")}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "30px",
              }}
            >
              <div className="app-w-200px">
                <strong> {authUser.business_name} </strong>
                <div>{authUser.business_address}</div>
                <div>{authUser.branch_name}</div>
                <div>{authUser.phone ? authUser.phone : ""}</div>
              </div>
              <div
                style={{
                  width: "200px",
                  textAlign: "center",
                }}
              >
                <div>
                  {isVatExempt ? "" : `${t("vat_number")}: ${authUser.vat}`}
                </div>
                <div>
                  {authUser.cr ? `${t("cr_number")}: ${authUser.cr}` : ""}
                </div>
              </div>

              <div
                style={{
                  width: "200px",
                  textAlign: "end",
                }}
              >
                {renderPrintableQRCode()}
                {renderPrintableZatcaStatus()}
              </div>
            </div>
            {/* list area */}

            <div
              style={{ borderBottom: "1px solid var(--color-text-muted)", paddingTop: "40px" }}
            />
            <div
              className="app-summary-row"
            >
              <div>
                <div>{t("description")}</div>
              </div>
              <div
                className="app-flex-between-center"
              >
                <div className="app-w-100px app-text-center">
                  <div>{t("qty")}</div>
                </div>

                <div className="app-w-100px app-text-center">
                  <div>{t("unit_price")}</div>
                </div>

                <div style={{ textAlign: "left", width: "120px" }}>
                  <div>{t("price_including_tax")}</div>
                </div>
              </div>
            </div>
            <div
              style={{ borderBottom: "1px dashed var(--color-text-muted)", paddingTop: "10px" }}
            />
            {data.items.map((item, i) => (
              <div
                className="app-summary-row"
                key={i}
              >
                <span>
                  {getLocalizedName(item)}
                  {item.tags && item.tags.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {item.tags.map((tag) => (
                        <Tag key={tag.id ?? tag} color="blue">
                          {getLocalizedName(tag)}
                        </Tag>
                      ))}
                    </div>
                  )}
                </span>
                <div
                  className="app-flex-between-center"
                >
                  <span className="app-w-100px app-text-center">
                    {item.quantity}
                  </span>
                  <span
                    className="app-w-100px app-text-center"
                  >
                    <CurrencyFormatter
                      value={item.price}
                      currencyCode={false}
                    />
                  </span>
                  <span
                    style={{
                      textAlign: "left",
                      width: "120px",
                    }}
                  >
                    <CurrencyFormatter
                      value={item.subtotal}
                      currencyCode={false}
                    />
                  </span>
                </div>
              </div>
            ))}
            <div
              style={{ borderBottom: "1px solid var(--color-text-muted)", paddingTop: "10px" }}
            />
            {/* Receipt total */}
            <div
              style={{
                maxWidth: "400px",
                border: "1px solid var(--color-text-muted)",
                marginTop: "30px",
                marginRight: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "5px 10px",
                }}
              >
                <span>{t("total_without_tax")}</span>
                <span>
                  <CurrencyFormatter value={data.amount_charged - data.tax} />
                </span>
              </div>
              <div
                style={{
                  padding: "0 10px",
                  borderBottom: "1px solid var(--color-text-muted)",
                }}
              >
                {<TaxCalculator data={data} />}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "5px 10px",
                  fontWeight: "bold",
                }}
              >
                <span>{isVatExempt ? t("total") : t("total_with_tax")}</span>
                <span>
                  <CurrencyFormatter value={data.amount_charged} />
                </span>
              </div>
            </div>
            {/* footer */}
            <div style={{ textAlign: "center", marginTop: "50px" }}>
              <small>{t("printed_by_anypos")}</small>
              <br />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewSalesInvoice;
